import type { BotDefeatRuntime } from "./bot-defeat-runtime";
import type { GameFlow, GameFlowSnapshot } from "./game-flow";
import type { ProgressState } from "./progress-state";
import { isTimedModeId, type SkinId } from "../config/gameplay-config";
import type {
  RoundSettlementReceipt,
  RoundSettlementStatus,
} from "./round-settlement";
import type { RoundRuntime, RoundRuntimeSnapshot } from "./round-runtime";
import type { ChallengeResultDefinition } from "./challenge-runtime";

export type ResultsSettlementStatus =
  "conflict" | "failed" | "inactive" | "settling" | RoundSettlementStatus;

export interface RoundResultModel extends RoundSettlementReceipt {
  readonly challenge?: ChallengeResultDefinition;
  readonly isNewBestLevel: boolean;
  readonly outcome: string;
  readonly personalBestLevel: number;
  readonly selectedSkinId: SkinId;
  readonly totalCoins: number;
}

export interface ResultsRuntimeSnapshot {
  readonly navigationAvailable: boolean;
  readonly result: RoundResultModel | null;
  readonly settlementStatus: ResultsSettlementStatus;
}

export type ResultsRuntimeListener = (snapshot: ResultsRuntimeSnapshot) => void;

export interface RoundSettlementPort {
  getState(): Pick<ProgressState, "personalBestLevels" | "totalCoins"> &
    Partial<Pick<ProgressState, "dailyChallengeRecords">>;
  settleRound(
    receipt: RoundSettlementReceipt,
  ):
    | Promise<RoundSettlementStatus | "failed">
    | RoundSettlementStatus
    | "failed";
}

interface ResultsPlayerPort {
  getSnapshot(): {
    readonly foodCollected: number;
    readonly player: {
      readonly level: number;
      readonly selectedSkinId: SkinId;
    } | null;
  };
}
type ResultsBotDefeatPort = Pick<BotDefeatRuntime, "getSnapshot">;
type ResultsRoundPort = Pick<RoundRuntime, "subscribe">;

export interface ResultsChallengePort {
  getActiveChallenge(): ChallengeResultDefinition | null;
  replay(challenge: ChallengeResultDefinition): boolean;
}

const cloneResult = (result: RoundResultModel): RoundResultModel =>
  result.challenge === undefined
    ? { ...result }
    : { ...result, challenge: { ...result.challenge } };

export class ResultsRuntime {
  private activeReceiptId: string | null = null;
  private readonly listeners = new Set<ResultsRuntimeListener>();
  private result: RoundResultModel | null = null;
  private settlementStatus: ResultsSettlementStatus = "inactive";

  public constructor(
    private readonly flow: GameFlow,
    round: ResultsRoundPort,
    private readonly player: ResultsPlayerPort,
    private readonly botDefeat: ResultsBotDefeatPort,
    private readonly settlement: RoundSettlementPort,
    private readonly createReceiptId: () => string,
    private readonly challengeRuntime: ResultsChallengePort,
  ) {
    flow.subscribe((snapshot) => {
      this.applyFlow(snapshot);
    });
    round.subscribe((snapshot) => {
      this.applyRound(snapshot);
    });
  }

  public getSnapshot(): ResultsRuntimeSnapshot {
    const navigationAvailable =
      this.settlementStatus === "settled" ||
      this.settlementStatus === "duplicate";
    return {
      navigationAvailable,
      result: this.result === null ? null : cloneResult(this.result),
      settlementStatus: this.settlementStatus,
    };
  }

  public playAgain(): boolean {
    const result = this.result;
    if (!this.getSnapshot().navigationAvailable || result === null) {
      return false;
    }
    return result.challenge === undefined
      ? (this.flow.startMode(result.mode), true)
      : this.challengeRuntime.replay(result.challenge);
  }

  public retrySettlement(): boolean {
    if (
      this.result === null ||
      (this.settlementStatus !== "failed" &&
        this.settlementStatus !== "conflict")
    ) {
      return false;
    }
    this.startSettlement(this.result);
    return true;
  }

  public showHome(): boolean {
    if (!this.getSnapshot().navigationAvailable) {
      return false;
    }
    this.flow.showHome();
    return true;
  }

  public subscribe(listener: ResultsRuntimeListener): () => void {
    this.listeners.add(listener);
    listener(this.getSnapshot());
    return () => {
      this.listeners.delete(listener);
    };
  }

  private applyFlow(snapshot: GameFlowSnapshot): void {
    if (snapshot.scene === "gameplay") {
      this.activeReceiptId = this.createReceiptId();
      this.result = null;
      this.settlementStatus = "inactive";
      this.notify();
      return;
    }
    if (snapshot.scene === "home") {
      this.activeReceiptId = null;
      this.result = null;
      this.settlementStatus = "inactive";
      this.notify();
    }
  }

  private applyRound(snapshot: RoundRuntimeSnapshot): void {
    const mode = this.flow.getSnapshot().mode;
    if (
      snapshot.result === null ||
      this.activeReceiptId === null ||
      this.result !== null ||
      mode === null ||
      !isTimedModeId(mode)
    ) {
      return;
    }
    const player = this.player.getSnapshot();
    const botDefeat = this.botDefeat.getSnapshot();
    const playerState = player.player;
    if (playerState === null) {
      return;
    }
    const finalLevel = playerState.level;
    const progress = this.settlement.getState();
    const challenge = this.challengeRuntime.getActiveChallenge();
    const challengeRecord =
      challenge === null
        ? undefined
        : progress.dailyChallengeRecords?.find(
            (record) => record.identity === challenge.identity,
          );
    const coinsEarned = botDefeat.pendingReward;
    const priorBestLevel =
      challenge === null
        ? progress.personalBestLevels[mode]
        : (challengeRecord?.bestLevel ?? 0);
    this.result = {
      ...(challenge === null ? {} : { challenge }),
      coinsEarned,
      finalLevel,
      foodCollected: player.foodCollected,
      id: this.activeReceiptId,
      isNewBestLevel: finalLevel > priorBestLevel,
      mode,
      outcome: challenge === null ? "Great run!" : challenge.resultsOutcome,
      personalBestLevel: Math.max(priorBestLevel, finalLevel),
      result: snapshot.result,
      selectedSkinId: playerState.selectedSkinId,
      snakesDefeated: botDefeat.snakesDefeated,
      totalCoins: progress.totalCoins,
    };
    this.startSettlement(this.result);
  }

  private notify(): void {
    for (const listener of this.listeners) {
      listener(this.getSnapshot());
    }
  }

  private startSettlement(result: RoundResultModel): void {
    const receipt: RoundSettlementReceipt = {
      ...(result.challenge === undefined
        ? {}
        : { challengeIdentity: result.challenge.identity }),
      coinsEarned: result.coinsEarned,
      finalLevel: result.finalLevel,
      foodCollected: result.foodCollected,
      id: result.id,
      mode: result.mode,
      result: result.result,
      snakesDefeated: result.snakesDefeated,
    };
    this.settlementStatus = "settling";
    this.notify();
    void Promise.resolve(this.settlement.settleRound(receipt)).then(
      (status) => {
        if (this.result?.id !== receipt.id) {
          return;
        }
        this.settlementStatus = status;
        this.result = {
          ...this.result,
          totalCoins: this.settlement.getState().totalCoins,
        };
        if (status === "settled" || status === "duplicate") {
          this.flow.showResults();
        }
        this.notify();
      },
    );
  }
}
